<template>
	<div class="mall-shop-page">
		<div v-loading="loading" class="shop-panel shop-profile-panel">
			<template v-if="shopInfo.id">
				<div class="shop-hero">
					<div class="shop-logo">
						<el-image v-if="logoUrl" :src="logoUrl" fit="cover"></el-image>
					</div>
					<div class="shop-hero-main">
						<div class="shop-title-row">
							<h3 class="shop-name">{{ shopInfo.shopName || '-' }}</h3>
							<el-button
								v-if="Number(shopInfo.shopState) === SHOP_STATE.NORMAL"
								type="text"
								icon="el-icon-edit"
								@click="handleToChange"
							></el-button>
							<el-tag size="small" :type="shopStateTagType">{{ shopStateText }}</el-tag>
							<el-button type="text" class="preview-btn">预览</el-button>
						</div>
						<div class="shop-meta muted">店铺ID：{{ shopInfo.shopCode || shopInfo.id || '-' }}</div>
						<div class="shop-meta-line">
							<span>联系人：{{ shopInfo.contactPerson || shopInfo.contactName || '-' }}</span>
							<span>联系方式：{{ shopInfo.contactPhone || shopInfo.telephone || '-' }}</span>
							<span>开店时长：{{ openDurationText }}</span>
						</div>
					</div>
					<el-button
						v-if="Number(shopInfo.shopState) === SHOP_STATE.NORMAL"
						type="text"
						class="close-shop-btn"
						icon="el-icon-switch-button"
						@click="handleToClose"
					>
						闭店申请
					</el-button>
				</div>

				<div class="section">
					<div class="section-title editable-title">
						店铺信息
						<i v-if="Number(shopInfo.shopState) === SHOP_STATE.NORMAL" class="el-icon-edit" @click="handleToChange"></i>
					</div>
					<div class="intro-row">
						<div class="intro-label">业务简介：</div>
						<div class="intro-box">
							{{ shopInfo.businessDesc || shopInfo.businessScope || '-' }}
							<span class="intro-count">{{ introLength }}/800</span>
						</div>
					</div>
				</div>

				<div class="section">
					<div class="section-title editable-title">
						主体资质
						<i v-if="Number(shopInfo.shopState) === SHOP_STATE.NORMAL" class="el-icon-edit" @click="handleToChange"></i>
					</div>
					<div class="subject-info">
						<div class="info-item">
							<span class="info-label">经营主体名称：</span>
							<span class="info-value">{{ shopInfo.companyName || shopInfo.subjectName || '-' }}</span>
						</div>
						<div class="info-item">
							<span class="info-label">经营地址：</span>
							<span class="info-value">{{ shopInfo.businessAddress || shopInfo.address || '-' }}</span>
						</div>
						<div class="info-item">
							<span class="info-label">开户行名称：</span>
							<span class="info-value">{{ shopInfo.bankName || '-' }}</span>
						</div>
						<div class="info-item">
							<span class="info-label">开户行账号：</span>
							<span class="info-value">{{ shopInfo.bankAccount || '-' }}</span>
						</div>
						<div class="info-item">
							<span class="info-label">企业营业执照：</span>
							<div v-if="businessLicenseFile" class="attachment-inline">
								<el-image
									v-if="businessLicenseFile.isImage"
									class="attachment-image"
									:src="businessLicenseFile.url"
									:preview-src-list="imagePreviewList"
									fit="cover"
								></el-image>
								<el-button v-else type="text" @click="handlePreviewFile(businessLicenseFile)">查看证件</el-button>
							</div>
							<span v-else class="empty-tip">暂无证件</span>
						</div>
					</div>
				</div>
			</template>

			<empty-content v-else empty-text="暂无店铺信息"></empty-content>
			<div v-if="!shopInfo.id" class="form-actions">
				<el-button type="primary" @click="handleNoShopAction">申请成为服务商</el-button>
			</div>
		</div>
	</div>
</template>

<script>
	import EmptyContent from '@/components/EmptyContent'
	import { previewMerchantFile, queryMerchantFileList, queryMyLatestShopApply, queryMyShop } from '@/api/mall'
	import { ENUM_SHOP_STATE, SHOP_AUDIT_STATE, SHOP_STATE } from '@/enum/index.js'

	export default {
		name: 'MallMyShop',
		components: {
			EmptyContent
		},
		data() {
			return {
				loading: false,
				shopInfo: {},
				fileList: [],
				SHOP_STATE
			}
		},
		computed: {
			logoUrl() {
				const logoFile = this.fileList.find(item => item.fileBusinessType === 'SHOP_LOGO')
				return logoFile && logoFile.fileId ? previewMerchantFile() + logoFile.fileId : ''
			},
			businessLicenseFile() {
				return this.fileList.find(item => item.fileBusinessType === 'BUSINESS_LICENSE')
			},
			imagePreviewList() {
				return this.fileList.filter(item => item.isImage && item.url).map(item => item.url)
			},
			introLength() {
				return (this.shopInfo.businessDesc || this.shopInfo.businessScope || '').length
			},
			openDurationText() {
				if (this.shopInfo.openDuration) return this.shopInfo.openDuration
				if (!this.shopInfo.openTime) return '-'
				const openTime = new Date(String(this.shopInfo.openTime).replace(/-/g, '/')).getTime()
				if (!openTime) return '-'
				const days = Math.max(Math.floor((Date.now() - openTime) / 86400000), 0)
				return `${days}天`
			},
			shopStateText() {
				return ENUM_SHOP_STATE[this.shopInfo.shopState] || '未知状态'
			},
			shopStateTagType() {
				if (Number(this.shopInfo.shopState) === SHOP_STATE.NORMAL) return 'success'
				if (Number(this.shopInfo.shopState) === SHOP_STATE.CLOSED) return 'info'
				return 'warning'
			}
		},
		created() {
			this.initPage()
		},
		methods: {
			async initPage() {
				this.loading = true
				try {
					const res = await queryMyShop().catch(() => ({ data: null }))
					this.shopInfo = res.data || {}
					if (this.shopInfo.id) {
						await this.loadFiles(this.shopInfo.id)
					}
				} finally {
					this.loading = false
				}
			},
			async loadFiles(businessId) {
				const res = await queryMerchantFileList({ businessId }).catch(() => ({ data: [] }))
				this.fileList = this.normalizeFiles(res.data)
			},
			normalizeFiles(files) {
				if (!Array.isArray(files)) return []
				return files.map(item => {
					const fileId = item.id || item.fileId || item.fileRecordId
					const name = item.fileName || item.name || item.originalName || '附件'
					const fileBusinessType = item.fileBusinessType
					return {
						fileId,
						name,
						fileBusinessType,
						url: fileId ? previewMerchantFile() + fileId : '',
						isImage: this.isImageFile({ ...item, name, fileBusinessType })
					}
				})
			},
			isImageFile(file) {
				const fileName = String(file.name || file.fileName || file.originalName || '').toLowerCase()
				const fileType = String(file.fileType || file.contentType || file.mimeType || '').toLowerCase()
				const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp']
				if (fileType.includes('image')) return true
				if (imageExts.some(ext => fileName.endsWith(ext))) return true
				return ['SHOP_LOGO', 'BUSINESS_LICENSE'].includes(file.fileBusinessType)
			},
			handlePreviewFile(item) {
				if (!item.fileId) return
				window.open(previewMerchantFile() + item.fileId, '_blank')
			},
			async getLatestPendingApply() {
				const res = await queryMyLatestShopApply().catch(() => ({ data: null }))
				const latestApply = res.data || null
				return latestApply && Number(latestApply.auditState) === SHOP_AUDIT_STATE.PENDING ? latestApply : null
			},
			async handleToChange() {
				const pendingApply = await this.getLatestPendingApply()
				if (pendingApply) {
					this.$message.warning('已有待审核申请，请等待平台审核')
					this.$router.push({ path: '/mall/shop/apply-detail', query: { id: pendingApply.id } })
					return
				}
				this.$router.push('/mall/shop/change')
			},
			async handleToClose() {
				const pendingApply = await this.getLatestPendingApply()
				if (pendingApply) {
					this.$message.warning('已有待审核申请，请等待平台审核')
					this.$router.push({ path: '/mall/shop/apply-detail', query: { id: pendingApply.id } })
					return
				}
				this.$router.push('/mall/shop/close')
			},
			async handleNoShopAction() {
				const res = await queryMyLatestShopApply().catch(() => ({ data: null }))
				const latestApply = res.data || null
				if (latestApply && Number(latestApply.auditState) === SHOP_AUDIT_STATE.PENDING) {
					this.$router.push({ path: '/mall/shop/apply-detail', query: { id: latestApply.id } })
					return
				}
				this.$router.push('/mall/shop/apply')
			}
		}
	}
</script>

<style lang="scss" scoped>
	@import './shop.scss';
</style>
